import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { from, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { CoreMenu, CoreMenuItem } from "@core/types";
import { fileVisualizeUrl, isIconClass } from "@core/utils";

import { environment } from "environments/environment";
import { Menu } from "@common/models";
import { AuthenticationTypeService } from "./authentication-type.service";

@Injectable({ providedIn: "root" })
export class MenuService {

  /** Api key de belivfiles, para los iconos que son imagen */
  private _apiKeyCache: string = null;

  /**
   *
   * @param {HttpClient} _http
   * @param {AuthenticationTypeService} _authenticationTypeService
   */
  constructor(
    private _http: HttpClient,
    private _authenticationTypeService: AuthenticationTypeService
  ) {}

  /**
   * Get all menus
   */
  getAll() {
    return this._http.get<Menu[]>(`${environment.commonApiUrl}/api/menus`);
  }

  /**
   * Get top ten menus lite
   */
  getTopTenLite() {
    return this._http.get<Menu[]>(
      `${environment.commonApiUrl}/api/menus/top-lite`
    );
  }

  /**
   * Obtiene un listado de menús a los que tiene acceso el usuario
   * @returns Devuelve una lista de menús
   */
  getMenuItems(superUser: boolean = false) {
    // La key se resuelve antes para poder armar la URL de los iconos que son
    // imagen; si falla se sigue igual (los iconos de clase no la necesitan).
    // Un superusuario obtiene todos los menús activos; para el resto se conserva
    // el endpoint filtrado por roles y compañía.
    const endpoint = superUser ? '/api/menus' : '/api/menu-items';

    return from(this._apiKey()).pipe(
        switchMap((apiKey) =>
            this._http.get<Menu[]>(`${environment.commonApiUrl}${endpoint}`).pipe(
                map((menus) => {
                  const items = superUser ? this.onlyNavigable(menus || []) : (menus || []);
                  let coreMenus = this.convertToCoreMenu(items, new Menu(), apiKey);
                  return coreMenus;
                })
            )
        )
    );
  }

  /**
   * `/api/menus` es el endpoint de mantenimiento: devuelve todas las filas de
   * la tabla, incluidas las que existen sólo para colgarles permisos
   * (`isMenu = false`, como Permisos o Accesos) y las inactivas. El filtrado
   * que `/api/menu-items` hace en el servidor hay que repetirlo aquí, o el
   * superusuario termina con esas filas en la navegación.
   *
   * Los campos ausentes se tratan como visibles: así un menú válido nunca
   * desaparece porque el backend haya omitido el estado.
   */
  private onlyNavigable(menus: Menu[]): Menu[] {
    return menus.filter(
      (menu) => menu.isMenu !== false && (menu.status?.code ?? "ACT") === "ACT"
    );
  }

  /**
   * Get menu by id
   */
  getById(id: number) {
    return this._http.get<Menu>(`${environment.commonApiUrl}/api/menus/${id}`);
  }

  /**
   * Get menu lite by title
   */
  getLiteByTitle(title: string) {
    return this._http.get<Menu[]>(
      `${environment.commonApiUrl}/api/menus/lite/${title}`
    );
  }

  /**
   * Devuelve Objeto de tipo Menu Lite
   */
  getByTitleLite(title: string) {
    return this._http.get<Menu[]>(
      `${environment.commonApiUrl}/api/menu/lite/${title}`
    );
  }

  addMenu(menu) {
    return this._http
      .post<Menu>(`${environment.commonApiUrl}/api/menus`, menu)
      .pipe(
        map((response: any) => response.menu as Menu),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  editMenu(menu) {
    return this._http
      .put<Menu>(`${environment.commonApiUrl}/api/menus/${menu.id}`, menu)
      .pipe(
        map((response: any) => response.menu as Menu),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  deleteMenu(id: number) {
    return this._http
      .put<Menu>(`${environment.commonApiUrl}/api/menus/${id}/ELI`, {})
      .pipe(
        map((response: any) => response.menu as Menu),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  /**
   * Función que sirve para convertir un array de menus a un array de CoreMenu
   * @param items Array de menus
   * @param node Objeto de tipo Menu
   * @param apiKey Key de belivfiles para los iconos que son imagen
   * @returns Devuelve un array de CoreMenu
   */
  private convertToCoreMenu(items: Menu[], node: Menu, apiKey: string = ""): CoreMenu[] {
    let result: CoreMenu[] = [];

    // Obtiene todos los menus padres
    let parents = items
      .filter(
        (menu) => (menu.parent == null ? new Menu() : menu.parent).id == node.id
      )
      .sort((menu) => menu.position);

    // Si el array de padres no es nulo y tiene valores
    if (parents != null && parents.length > 0) {
      // Reocorre el array de menu padres
      parents.forEach((parent) => {
        // Obtiene los hijos del padre
        let children = this.convertToCoreMenu(items, parent, apiKey) as CoreMenuItem[];

        // Los menús marcados como "incrustar" guardan el sitio externo en link;
        // la navegación va a la ruta interna que lo muestra embebido
        const embedded = parent.isEmbedded === true && !!parent.link;

        // Las acciones no navegan: al elegirlas se hace la petición a link con
        // el verbo de mapping_type (ver MenuActionService)
        const action = parent.isAction === true && !!parent.mappingType && !!parent.link;

        // Crea un menu de tipo CoreMenu
        let menu: CoreMenu = {
          id: parent.id.toString(),
          title: parent.title,
          type:
            children == null || children.length == 0 ? "item" : "collapsible",
          url: embedded ? `embedded/${parent.id}` : parent.link,
          icon: this.resolveIcon(parent.icon, apiKey),
          description: parent.description,
          descriptionEnglish: parent.descriptionEnglish,
          openInNewTab: embedded || action ? false : parent.isBlank,
          externalUrl: embedded || action ? false : parent.isBlank,
          translate: parent.titleEnglish,
          embeddedUrl: embedded ? parent.link : undefined,
          actionMethod: action ? parent.mappingType : undefined,
          backgroundUrl: parent.backgroundUrl,
          children: children,
        };
        // Lo agrega a la lista de menus
        result.push(menu);
      });
    }

    console.log("MenuService.convertToCoreMenu", result);

    // Devuelve los menús padres
    return result;
  }

  /**
   * El icono del menú puede ser una clase de fuente ('fa fa-home') o una
   * imagen. Si es imagen se devuelve la URL lista para usar en un <img>, así
   * ninguna pantalla necesita la api key para pintarlo.
   * @param icon Valor guardado en el menú
   * @param apiKey Key de belivfiles
   */
  private resolveIcon(icon: string, apiKey: string): string {
    if (icon == null || icon.trim() === "") return "circle";
    if (isIconClass(icon)) return icon;
    return fileVisualizeUrl(icon, apiKey) || icon;
  }

  /** Obtiene (una sola vez) la key de belivfiles */
  private async _apiKey(): Promise<string> {
    if (this._apiKeyCache != null) return this._apiKeyCache;
    try {
      this._apiKeyCache = ((await this._authenticationTypeService.getApiKey()) as string) || "";
    } catch {
      this._apiKeyCache = "";
    }
    return this._apiKeyCache;
  }

  getLiteByUrl(url: string) {
    return this._http.get<Menu>(
      `${environment.commonApiUrl}/api/menu/lite?url=${url}`
    );
  }
}
